import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Compass, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function CreatePassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const setupToken =
    sessionStorage.getItem("cgps_email_setup_token") ||
    params.get("setup_token") ||
    "";

  // IMPORTANT:
  // For email OTP/reset flows, always use the email stored
  // by the current verification session.
  const setupEmail =
    sessionStorage.getItem("cgps_email_setup_email") ||
    "";

  const displayEmail = setupEmail || user?.email || "";

  const submit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }

    if (!setupToken && !user) {
      toast.error(
        "Your password setup session has expired. Please start again."
      );
      nav("/login", { replace: true });
      return;
    }

    setLoading(true);

    try {
      const endpoint = setupToken
        ? "/auth/complete-email-signup"
        : "/auth/set-password";

      const { data } = await api.post(endpoint, {
        new_password: password,
        ...(setupToken
          ? { setup_token: setupToken }
          : {}),
      });

      setUser(data.user);

      sessionStorage.removeItem(
        "cgps_email_setup_token"
      );
      sessionStorage.removeItem(
        "cgps_email_setup_email"
      );

      toast.success("Password created successfully.");

      nav(
        data.user.onboarded
          ? "/dashboard"
          : "/onboarding",
        { replace: true }
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Unable to create password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex flex-col"
      data-testid="create-password-page"
    >
      <div className="px-6 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2"
        >
          <Compass
            size={22}
            weight="duotone"
            className="text-blue-500"
          />

          <span className="font-medium tracking-tight">
            Career GPS{" "}
            <span className="text-blue-500">AI</span>
          </span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 text-blue-400 mb-5">
            <LockKey size={25} weight="duotone" />
          </div>

          <h1 className="text-4xl tracking-tight font-medium">
            Create your password
          </h1>

          <p className="text-zinc-500 mt-2">
            You'll use this password to sign in next time.
          </p>

          {displayEmail && (
            <p className="text-sm text-zinc-400 mt-4">
              Account:{" "}
              <span className="text-white">
                {displayEmail}
              </span>
            </p>
          )}

          <form
            onSubmit={submit}
            className="mt-8 space-y-4"
          >
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest">
                Password
              </label>

              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) =>
                  setPassword(e.target.value)
                }
                data-testid="create-password-input"
                className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-md px-4 py-3 text-white outline-none"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest">
                Confirm password
              </label>

              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) =>
                  setConfirm(e.target.value)
                }
                data-testid="create-password-confirm-input"
                className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-md px-4 py-3 text-white outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="create-password-submit-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors"
            >
              {loading
                ? "Saving password..."
                : "Create password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
